import { Component, ElementRef, Input, OnInit, QueryList, ViewChildren } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { fillScreenAnimation } from '../shared/animations/animations';
import { CardListItemClickService } from '../card-list-item/card-list-item-click.service';
import { DeviceInformation } from '../shared/models/device-information';
import { CardData } from '../device-model/model/card-data';
import { GraphicCardData } from '../device-model/model/graphic-card-data';
import { SectionCardData } from '../device-model/model/section-card-data';
import { GraphicBehaviorIconCreator } from '../display-info-icon/graphic-behavior-icon-creator';
import { ICardDataViewModel, IDisplayInfo } from '../shared/models/models';
import { GraphicBehaviorType } from '../shared/models/graphic-behavior-type.enum';

@Component({
    selector: 'dl-card-display-info',
    templateUrl: './card-display-info.component.html',
    styleUrls: ['./card-display-info.component.scss', './common-list-items.scss'],
    animations: [fillScreenAnimation]
})
export class CardDisplayInfoComponent implements OnInit {
    public static $name: string = 'cardDisplayInfo';
    static NEST_SLIDER_RELATED_ITEMS = [
        GraphicBehaviorType.TEMPERATURE_SENSOR,
        GraphicBehaviorType.OCCUPIED_SENSOR,
        GraphicBehaviorType.TEMPERATURE_COLD_SENSOR,
        GraphicBehaviorType.TEMPERATURE_COLD_UNOCCUPIED_SENSOR,
        GraphicBehaviorType.TEMPERATURE_HOT_SENSOR,
        GraphicBehaviorType.TEMPERATURE_HOT_UNOCCUPIED_SENSOR,
    ];

    @Input() displayInfo: IDisplayInfo;
    @Input() displayInfoForEdit: IDisplayInfo;
    @Input() set isEditMode(val: boolean) {
        this._isEditMode = val;
        this.refreshCurrentDisplayInfo();
    }
    get isEditMode() {
        return this._isEditMode;
    }
    @Input() dashboardEditModel: DeviceInformation;
    @Input() form: FormGroup;
    @Input() heating: boolean;
    @Input() cooling: boolean;
    @Input() rowMode: boolean;
    @Input() animate: boolean = false;
    @Input() setDeviceInfo: (deviceInfo: DeviceInformation) => void;

    @ViewChildren('displayInfo', { read: ElementRef }) public elements: QueryList<ElementRef>;

    itemsAreHidden = true;

    isMoreLessButtonNeeded = false;
    MORE_LESS_BUTTON_THRESHOLD = 5;
    hiddenCount = 0;
    selectedCard: ICardDataViewModel;

    startAnimationTop: 0 | string = 0;
    startAnimationHeight: 0 | string = 0;
    startAnimationWidth: 0 | string = 0;
    animationScrollTop: 0 | string = 0;
    currentDisplayInfoViewModel: ICardDataViewModel[];
    currentDisplayInfo: IDisplayInfo;

    private startAnimationLeft: 0 | string = 0;
    private _isEditMode: boolean;

    constructor(private cardListItemClickedService: CardListItemClickService) {
    }

    ngOnInit() {
        this.refreshCurrentDisplayInfo();
    }

    refreshOutOfSyncData() {
        setTimeout(() => {
            this.refreshCurrentDisplayInfo();
        });
    }

    getDeviceInput(item: ICardDataViewModel) {
        if (this.isEditMode &&
            this.dashboardEditModel &&
            this.dashboardEditModel.$deviceHierarchy &&
            this.displayInfoForEdit &&
            item &&
            item.$controlInput &&
            item.$controlInput.$inputType) {
            item.$controlInput.displayText = '';
            return item.$controlInput;
        }
        return null;
    }

    showMoreItems() {
        this.itemsAreHidden = false;
        this.refreshCurrentDisplayInfo();
    }

    moreButtonVisible() {
        return this.isMoreLessButtonNeeded && this.itemsAreHidden;
    }

    showLessItems() {
        this.itemsAreHidden = true;
        this.refreshCurrentDisplayInfo();
    }

    lessButtonVisible() {
        return this.isMoreLessButtonNeeded && !this.itemsAreHidden;
    }

    getText(item: GraphicCardData) {
        let displayIcon = new GraphicBehaviorIconCreator(null).createGraphicBehavior(item);
        if (!displayIcon) {
            return '';
        }
        return displayIcon && displayIcon.isLabelVisible() ? displayIcon.getLabelText() + (displayIcon.getUnits ? '' + displayIcon.getUnits() : '') : '';
    }

    fireClickEvent(item: ICardDataViewModel) {
        this.cardListItemClickedService.cardClickedEvent.emit(item);
    }

    indicateCardClicked = (index: number, card: ICardDataViewModel) => {
        if (!this.animate) {
            this.fireClickEvent(card);
            return;
        }

        const components = this.elements;
        this.startAnimationTop = 0;
        this.startAnimationLeft = 0;
        components.forEach((item, i) => {
            if (index === i) {
                const cardElement = item.nativeElement;
                const parentElement = cardElement.parentElement.parentElement.parentElement.parentElement.parentElement.parentElement.parentElement.parentElement;
                this.startAnimationLeft = `${cardElement.offsetLeft}px`;
                this.startAnimationTop = `${cardElement.offsetTop}px`;
                this.startAnimationHeight = `${cardElement.offsetHeight}px`;
                this.startAnimationWidth = `${cardElement.offsetWidth}px`;
                this.animationScrollTop = `${parentElement.scrollTop}px`;
                return;
            }
        });
        this.selectedCard = card;
    }

    shouldItemBeHidden(item: ICardDataViewModel) {
        if (item?.graphicBehavior) {
            if (this.rowMode) {
                return false;
            }
            if (!this.isEditMode) {
                return false;
            }
            if (CardDisplayInfoComponent.NEST_SLIDER_RELATED_ITEMS.includes(item.graphicBehavior as GraphicBehaviorType)) {
                return true;
            }
        }
        return false;
    }

    private shouldBeVisibleItems(arr: ICardDataViewModel[]) {
        return arr.filter(item => {
            return !this.shouldItemBeHidden(item);
        });
    }

    private getVisibleDisplayInfoDataViewModel() {
        if (!this.displayInfo.dataViewModel) {
            return [];
        }
        let toFilter = this.currentDisplayInfo?.dataViewModel;
        toFilter = toFilter.filter(it => {
            return !(it instanceof SectionCardData) && (it instanceof CardData || it instanceof GraphicCardData);
        });

        let visibleItems = this.shouldBeVisibleItems(toFilter);
        const lengthOfVisibleItems = visibleItems.length;

        if (lengthOfVisibleItems > this.MORE_LESS_BUTTON_THRESHOLD && !this.rowMode) {
            this.isMoreLessButtonNeeded = true;
            if (this.itemsAreHidden) {
                this.hiddenCount = lengthOfVisibleItems - this.MORE_LESS_BUTTON_THRESHOLD;
                visibleItems = visibleItems.slice(0, this.MORE_LESS_BUTTON_THRESHOLD);
            }
        }
        return visibleItems;
    }

    private refreshCurrentDisplayInfo() {
        this.currentDisplayInfo = this.isEditMode && this.displayInfoForEdit ? this.displayInfoForEdit : this.displayInfo;
        this.currentDisplayInfoViewModel = this.getVisibleDisplayInfoDataViewModel();
    }
}
